import {
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Group,
  Pagination,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconFilterOff, IconPlus } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client';
import { personsApi, personsExportHref, type PersonsListParams } from '../api/persons';
import type { LifecycleStatus, Person, PersonKind } from '../api/types';
import { useAlertModal } from '../components/AlertModalProvider';
import { useCurrentUser } from '../hooks/useAuth';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';
import { ExportCsvButton } from '../components/ExportCsvButton';
import { usePermissions } from '../hooks/usePermissions';
import { personsKeys, usePersons } from '../hooks/usePersons';
import {
  COMMS_FILTER_OPTIONS, COMMS_TYPES, commsValueLabel, type CommsField,
} from '../utils/commsPreferences';
// useDeletePerson / useRestorePerson / useConfirmDelete removed 2026-06-02
// (Task #245) — destructive actions live exclusively on the detail page.
import {
  ColumnMultiSelectFilter, ColumnTextFilter,
  hasActiveFilters,
} from '../components/ColumnFilters';
import { PepDot, UspDot } from '../components/PepUsDot';
import { PEP_OPTIONS } from '../utils/pepLabels';
import { PendingCRBadge } from '../components/PendingCRBadge';
import { NO_STATUS_SELECTED_HINT, StatusFilterPills } from '../components/StatusFilterPills';
import {
  SortableTh, nextSort, sortParam, type SortState,
} from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';

const PAGE_SIZE = 50;
type PersonSortKey =
  | 'name' | 'agreement' | 'kind' | 'email' | 'tin' | 'agreement_roles' | 'account_roles'
  | 'policy_roles' | 'pep' | 'usp' | 'status' | CommsField;

export function PersonsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  // #39 (2026-06-11) + B2-8 (2026-07-08): status pills. Persons have no stored
  // lifecycle, but B2-8 gives a DERIVED split — Active = holds a role on an Active
  // (or, P13, Prospect) agreement / Active account / policy; Inactive = alive but no such role (decluttered by
  // default, retained + searchable). Deleted = the soft-delete bucket. Union
  // semantics (C-132), like the lifecycle list pages. Default = Active only.
  const [statuses, setStatuses] = useState<LifecycleStatus[]>(['Active']);
  const [deletedSelected, setDeletedSelected] = useState(false);
  const hasSelection = statuses.length > 0 || deletedSelected;

  // ── #46 Wave 1 (2026-06-12): per-column predicate filters (C-132) ──────
  // These replaced the old global search box (Name column owns it now)
  // and the kind Select (Kind column owns it). Every control traces to a
  // query param (C-131); page resets to 1 on every change.
  // Closed-set filters are multi-select (C-132 amendment 2026-06-12).
  const [nameQ, setNameQ] = useState('');
  const [kindF, setKindF] = useState<string[]>([]);
  const [emailQ, setEmailQ] = useState('');
  const [tinQ, setTinQ] = useState('');
  const [pepF, setPepF] = useState<string[]>([]);
  const [uspF, setUspF] = useState<string[]>([]);
  // Pedro 2026-10-10: the Agreement column — text filter + sort (name → A→Z → Z→A → name).
  const [agreementQ, setAgreementQ] = useState('');
  // P41 (2026-10-15): every header sorts (server-side, across pages); null = by name.
  const [sort, setSortState] = useState<SortState<PersonSortKey> | null>(null);
  function onSort(k: PersonSortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }

  // P13 (Pedro 2026-10-10): receives / doesn't filter per communication type.
  const NO_COMMS_FILTERS: Record<CommsField, string[]> = {
    comms_monthly_report: [], comms_newsletter: [],
    comms_event_invitations: [], comms_commercial_offers: [],
  };
  const [commsF, setCommsF] = useState<Record<CommsField, string[]>>(NO_COMMS_FILTERS);
  const commsActive = Object.values(commsF).some((v) => v.length > 0);

  const filtersActive = commsActive
    || hasActiveFilters({ nameQ, kindF, emailQ, tinQ, pepF, uspF, agreementQ });
  function clearFilters() {
    setNameQ(''); setKindF([]); setEmailQ(''); setTinQ('');
    setPepF([]); setUspF([]); setAgreementQ(''); setCommsF(NO_COMMS_FILTERS);
    setPage(1);
  }
  function paged<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  const { user } = useCurrentUser();
  const { can } = usePermissions();
  const canWrite = can('record.write');
  const autoApplies = user?.role === 'admin' || user?.role === 'compliance';

  // ── P13 email preferences: STAGED edits (Pedro 2026-10-10) ──────────────
  // Ticking a box no longer opens a dialog: each change is staged (orange cell),
  // and the header shows "N unsaved" + Discard / Save changes — the Securities
  // grid's batched-edit shape (C-295). Leaving the page with staged changes opens
  // the guard, which offers Save & leave / Discard & leave / Stay. Staged changes
  // survive paging and filtering (keyed by person id). Saving sends ONE update per
  // person with all its changed flags: Admin / Compliance apply directly, an RM's
  // change is filed for approval (default maker-checker).
  const qc = useQueryClient();
  const { showAlert } = useAlertModal();
  const [staged, setStaged] = useState<StagedComms>({});
  const [savingComms, setSavingComms] = useState(false);
  const stagedCount = Object.values(staged)
    .reduce((n, s) => n + Object.keys(s.fields).length, 0);
  const stagedNames = Object.values(staged).map((s) => s.name).join(', ');

  function stageComms(p: Person, field: CommsField, next: boolean) {
    setStaged((prev) => {
      const fields = { ...(prev[p.id]?.fields ?? {}) };
      if (next === p[field]) delete fields[field];   // back to the saved value
      else fields[field] = next;
      const n = { ...prev };
      if (Object.keys(fields).length === 0) delete n[p.id];
      else n[p.id] = { name: p.display_name, fields };
      return n;
    });
  }

  async function saveComms(): Promise<boolean> {
    const entries = Object.entries(staged);
    if (entries.length === 0) return true;
    setSavingComms(true);
    let applied = 0;
    let filed = 0;
    const saved: string[] = [];
    const failed: string[] = [];
    for (const [id, s] of entries) {
      try {
        const r = await personsApi.update(id, s.fields);
        if (r.pending) filed += 1; else applied += 1;
        saved.push(id);
      } catch (e) {
        failed.push(`${s.name}: ${e instanceof ApiError ? e.detail : String(e)}`);
      }
    }
    // Saved people leave the staged set; a failed one stays staged (orange) to retry.
    setStaged((prev) => {
      const n = { ...prev };
      for (const id of saved) delete n[id];
      return n;
    });
    await qc.invalidateQueries({ queryKey: personsKeys.all });
    setSavingComms(false);
    if (saved.length > 0) {
      const parts = [
        applied ? `saved for ${people(applied)}` : '',
        filed ? `filed for approval for ${people(filed)}` : '',
      ].filter(Boolean);
      notifications.show({
        color: filed ? 'blue' : 'green',
        title: 'Email preferences',
        message: `Changes ${parts.join(' · ')}.`,
      });
    }
    if (failed.length > 0) {
      showAlert({
        color: 'red',
        title: 'Some email-preference changes could not be saved',
        message: `${failed.join(' · ')}. These changes are still unsaved (orange).`,
      });
    }
    return failed.length === 0;
  }

  // Navigating away with staged changes → the guard offers Save & leave.
  useDirtyFormGuard(stagedCount > 0, saveComms);

  const listParams: PersonsListParams = {
    kind: kindF.length > 0 ? (kindF as PersonKind[]) : undefined,
    q: nameQ || undefined,
    email_q: emailQ || undefined,
    tin_q: tinQ || undefined,
    pep_status: pepF.length > 0 ? pepF : undefined,
    // Yes+No = "answered" (excludes blanks) — C-132 amendment.
    us_person: uspF.length > 0 ? uspF.map((v) => v === 'true') : undefined,
    agreement_q: agreementQ || undefined,
    ...Object.fromEntries(COMMS_TYPES.map(({ field }) => [
      field, commsF[field].length > 0 ? commsF[field].map((v) => v === 'true') : undefined,
    ])),
    sort: sortParam(sort),
    // Union of the selected buckets (C-132): Deleted alone → only_deleted;
    // any alive pill + Deleted → include_deleted; alive alone → neither.
    include_deleted: statuses.length > 0 && deletedSelected ? true : undefined,
    only_deleted: statuses.length === 0 && deletedSelected ? true : undefined,
    // B2-8: which alive sub-population — both pills = 'all', else the one picked.
    alive: statuses.length > 0
      ? (statuses.length === 2 ? 'all' : statuses.includes('Inactive') ? 'inactive' : 'active')
      : undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  };
  const { data, isLoading, error } = usePersons(listParams, { enabled: hasSelection });
  // P13: the Admin-only CSV mirrors the list as filtered (same params, no paging).
  const exportHref = personsExportHref(listParams);

  // deletePerson / restorePerson / confirmDelete removed (Task #245, 2026-06-02).

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Container size="xl">
      <Stack>
        <Group justify="space-between">
          <Title order={2}>Persons</Title>
          <Group gap="sm">
            {/* P13: staged email-preference changes (orange) — commit or undo them all. */}
            {stagedCount > 0 && (
              <>
                <Tooltip label={`Unsaved changes for: ${stagedNames}`} withArrow multiline w={320}>
                  <Badge color="orange" variant="light">{stagedCount} unsaved</Badge>
                </Tooltip>
                <Button variant="default" disabled={savingComms} onClick={() => setStaged({})}>
                  Discard
                </Button>
                <Button loading={savingComms} onClick={() => void saveComms()}
                  title={autoApplies ? undefined : 'Your changes will be filed for approval'}>
                  Save changes
                </Button>
              </>
            )}
            {/* P13: export the list as filtered (e.g. everyone receiving the monthly
                report), with emails. Bulk exports are Admin-only (C-335): greyed with
                the reason for everyone else (§3h), like the Agreements list. */}
            <ExportCsvButton href={exportHref} hasSelection={hasSelection} />
            {/* P16b G1: split existing names into given names + surnames (one-off review). */}
            <Button variant="light" onClick={() => navigate('/persons/name-review')}>
              Name review
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/persons/new')}>
              New person
            </Button>
          </Group>
        </Group>

        {/* #46 (2026-06-12): the old global search box + kind Select were
            replaced by the per-column header filters below (C-132). */}

        {/* #39 (2026-06-11) + B2-8 (2026-07-08): status pills — Active / Inactive
            (DERIVED: Inactive = alive but no role on any Active or Prospect agreement /
            Active account / policy) + the Deleted bucket. Default = Active only. */}
        <StatusFilterPills
          entity="person"
          availableStatuses={['Active', 'Inactive']}
          // P46 (Pedro 2026-10-15): a person with no role is "No current role", not "Inactive".
          labels={{ Inactive: 'No current role' }}
          statuses={statuses}
          onStatusesChange={(next) => { setStatuses(next); setPage(1); }}
          includeDeleted={deletedSelected}
          onIncludeDeletedChange={(next) => { setDeletedSelected(next); setPage(1); }}
        />

        {error && <Text c="red">{error.detail}</Text>}

        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              {/* P41 (2026-10-15): every header sorts (server-side, across pages). */}
              <SortableTh label="Name" col="name" sort={sort} onSort={onSort} filtered={!!nameQ} />
              <SortableTh label="Agreement" col="agreement" sort={sort} onSort={onSort} filtered={!!agreementQ} />
              <SortableTh label="Kind" col="kind" sort={sort} onSort={onSort} filtered={kindF.length > 0} w={120} />
              <SortableTh label="Email" col="email" sort={sort} onSort={onSort} filtered={!!emailQ} />
              <SortableTh label="NIF / TIN" col="tin" sort={sort} onSort={onSort} filtered={!!tinQ} />
              {/* #40/old-#408 (2026-06-11): roles roll-up columns — PARTY-ROW counts,
                  same semantics as the Person detail header SummaryBox. */}
              <SortableTh label="Agreem." col="agreement_roles" sort={sort} onSort={onSort} w={95} />
              <SortableTh label="Accounts" col="account_roles" sort={sort} onSort={onSort} w={95} />
              <SortableTh label="Policies" col="policy_roles" sort={sort} onSort={onSort} w={95} />
              {/* Beta v3 polish B: per-Person PEP / USP dots. */}
              <SortableTh label="PEP" col="pep" sort={sort} onSort={onSort} filtered={pepF.length > 0} w={70} />
              <SortableTh label="USP" col="usp" sort={sort} onSort={onSort} filtered={uspF.length > 0} w={70} />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={200} />
              {/* P13: email preferences, one column per type (short header + full name);
                  at the end of the row since the 2026-10-11 smoke (S6, Pedro). */}
              {COMMS_TYPES.map(({ field, short }) => (
                <SortableTh key={field} label={short} col={field} sort={sort} onSort={onSort}
                  filtered={commsF[field].length > 0} w={90} />
              ))}
              {/* Actions column removed 2026-06-02 (Task #245) — delete/restore
                  moved to the detail page top-right exclusively. */}
            </Table.Tr>
            {/* #46 Wave 1 (2026-06-12): per-column filter row (C-132).
                Role-count columns are derived rollups (no server filter);
                Status is owned by the bucket pills. */}
            <Table.Tr>
              <Table.Th>
                <ColumnTextFilter value={nameQ} onChange={paged(setNameQ)}
                  placeholder="Name…" ariaLabel="Filter by name" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={agreementQ} onChange={paged(setAgreementQ)}
                  placeholder="CIIF-… / mnemonic" ariaLabel="Filter by agreement" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={kindF} onChange={paged(setKindF)}
                  options={[
                    { value: 'NATURAL', label: 'Natural' },
                    { value: 'LEGAL', label: 'Legal' },
                  ]}
                  allLabel="All" ariaLabel="Filter by kind" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={emailQ} onChange={paged(setEmailQ)}
                  placeholder="Email…" ariaLabel="Filter by email" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={tinQ} onChange={paged(setTinQ)}
                  placeholder="NIF / NIPC…" ariaLabel="Filter by NIF or TIN" />
              </Table.Th>
              <Table.Th />
              <Table.Th />
              <Table.Th />
              <Table.Th>
                <ColumnMultiSelectFilter values={pepF} onChange={paged(setPepF)}
                  options={PEP_OPTIONS}
                  allLabel="All" ariaLabel="Filter by PEP status" />
              </Table.Th>
              <Table.Th>
                {/* Yes+No = "answered" (excludes blank fiches). */}
                <ColumnMultiSelectFilter values={uspF} onChange={paged(setUspF)}
                  options={[
                    { value: 'false', label: 'No' },
                    { value: 'true', label: 'Yes' },
                  ]}
                  allLabel="All" ariaLabel="Filter by US person" />
              </Table.Th>
              <Table.Th>
                {filtersActive && (
                  <Tooltip label="Clear all column filters" withArrow>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      leftSection={<IconFilterOff size={14} />}
                      onClick={clearFilters}>
                      Clear
                    </Button>
                  </Tooltip>
                )}
              </Table.Th>
              {COMMS_TYPES.map(({ field, label }) => (
                <Table.Th key={field}>
                  <ColumnMultiSelectFilter values={commsF[field]}
                    onChange={paged((v: string[]) => setCommsF((prev) => ({ ...prev, [field]: v })))}
                    options={COMMS_FILTER_OPTIONS}
                    allLabel="All" ariaLabel={`Filter by ${label}`} />
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {/* #39: zero pills → hint instead of data (no fetch fired). */}
            {!hasSelection && (
              <Table.Tr><Table.Td colSpan={15}><Text c="dimmed">{NO_STATUS_SELECTED_HINT}</Text></Table.Td></Table.Tr>
            )}
            {hasSelection && isLoading && (
              <Table.Tr><Table.Td colSpan={15}><Text c="dimmed">Loading…</Text></Table.Td></Table.Tr>
            )}
            {/* #46 / C-132: two-tier empty state. */}
            {hasSelection && data && data.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={15}>
                  {filtersActive ? (
                    <Group gap="xs" justify="center" py="sm">
                      <Text c="dimmed" size="sm">No persons match the current filters.</Text>
                      <Button size="compact-xs" variant="subtle"
                        leftSection={<IconFilterOff size={14} />}
                        onClick={clearFilters}>
                        Clear filters
                      </Button>
                    </Group>
                  ) : (
                    <Text c="dimmed">No persons found.</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {hasSelection && data?.items.map((p) => (
              <Table.Tr
                key={p.id}
                style={{ cursor: 'pointer', opacity: p.deleted_at ? 0.5 : 1 }}
                onClick={() => navigate(`/persons/${p.id}`)}
              >
                <Table.Td><Text fw={500}>{p.display_name}</Text></Table.Td>
                <Table.Td>
                  {p.agreements?.length ? (
                    <Stack gap={0}>
                      {p.agreements.map((a) => (
                        <Anchor key={a.agreement_id} component={Link} to={`/agreements/${a.agreement_id}`}
                          size="sm" c={a.status === 'Active' ? undefined : 'dimmed'}
                          style={{ whiteSpace: 'nowrap' }}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                          {a.label}{a.status !== 'Active' ? ` (${a.status})` : ''}
                        </Anchor>
                      ))}
                    </Stack>
                  ) : <Text size="sm" c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={p.kind === 'LEGAL' ? 'grape' : 'blue'}>
                    {p.kind === 'LEGAL' ? 'Legal' : 'Natural'}
                  </Badge>
                </Table.Td>
                <Table.Td>{p.email ?? '—'}</Table.Td>
                <Table.Td>{p.tin_1 ?? p.nipc ?? '—'}</Table.Td>
                {/* #40/old-#408: counts; zero renders dimmed. */}
                <Table.Td ta="center">
                  <Text size="sm" c={p.agreement_role_count ? undefined : 'dimmed'}>
                    {p.agreement_role_count ?? 0}
                  </Text>
                </Table.Td>
                <Table.Td ta="center">
                  <Text size="sm" c={p.account_role_count ? undefined : 'dimmed'}>
                    {p.account_role_count ?? 0}
                  </Text>
                </Table.Td>
                <Table.Td ta="center">
                  <Text size="sm" c={p.policy_role_count ? undefined : 'dimmed'}>
                    {p.policy_role_count ?? 0}
                  </Text>
                </Table.Td>
                {/* Pedro 2026-10-13: a company is never a PEP — no dot, just "—". */}
                <Table.Td ta="center">
                  {p.kind === 'LEGAL'
                    ? <Text size="sm" c="dimmed" title="PEP applies to individuals only">—</Text>
                    : <PepDot value={p.pep_status} />}
                </Table.Td>
                <Table.Td ta="center"><UspDot value={p.us_person} /></Table.Td>
                <Table.Td>
                  <Stack gap={4} align="flex-start">
                    {/* P47: the badge explains its status on hover (same words as the pills). */}
                    <StatusExplained entity="person"
                      status={p.deleted_at ? 'Deleted' : p.has_active_role === false ? 'Inactive' : 'Active'}>
                    {p.deleted_at
                      ? <Badge color="gray" variant="light">Deleted</Badge>
                      : p.has_active_role === false
                        ? <Badge color="yellow" variant="light">No current role</Badge>
                        : <Badge color="green" variant="light">Active</Badge>}
                    </StatusExplained>
                    <PendingCRBadge crs={p.pending_change_requests} />
                  </Stack>
                </Table.Td>
                {COMMS_TYPES.map(({ field, label }) => {
                  const stagedValue = staged[p.id]?.fields[field];
                  return (
                    // The whole cell swallows the click, so a near-miss on a checkbox
                    // doesn't open the person. A staged change tints the cell orange
                    // (the Securities grid's staged-cell colour).
                    <Table.Td key={field} ta="center"
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'default', background: stagedValue !== undefined ? 'var(--mantine-color-orange-1)' : undefined }}>
                      <CommsCell person={p} field={field} label={label}
                        value={stagedValue ?? p[field]} staged={stagedValue !== undefined}
                        canWrite={canWrite} saving={savingComms}
                        onToggle={(next) => stageComms(p, field, next)} />
                    </Table.Td>
                  );
                })}
                {/* Actions column removed (Task #245 2026-06-02). */}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {data && data.total > PAGE_SIZE && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{data.total} total</Text>
            <Pagination total={totalPages} value={page} onChange={setPage} />
          </Group>
        )}
      </Stack>
    </Container>
  );
}

/** Staged email-preference changes, keyed by person id (P13, Pedro 2026-10-10). */
type StagedComms = Record<string, { name: string; fields: Partial<Record<CommsField, boolean>> }>;

const people = (n: number): string => `${n} ${n === 1 ? 'person' : 'people'}`;

/**
 * P13 (Pedro 2026-10-10): one email-preference checkbox on a Persons-list row. A click
 * STAGES the change (no dialog); the page commits all staged changes with Save changes,
 * or on leaving the page via the guard's Save & leave (§3g: pending until saved; §3j:
 * orange = staged, blue dot = filed and awaiting approval). A pending change to THIS flag
 * shows the blue dot; staging another still works, and the tooltip warns that saving
 * files a second request (C-128: warn, never block).
 */
function CommsCell({ person, field, label, value, staged, canWrite, saving, onToggle }: {
  person: Person; field: CommsField; label: string;
  value: boolean; staged: boolean; canWrite: boolean; saving: boolean;
  onToggle: (next: boolean) => void;
}) {
  const pending = (person.pending_change_requests ?? [])
    .filter((cr) => (cr.changed_fields ?? []).includes(field));
  const filedBy = pending[0]?.requested_by_name ?? 'someone';
  const reason = person.deleted_at ? 'Deleted person — restore it to change preferences.'
    : !canWrite ? 'Your role cannot change records.' : undefined;

  const box = (
    <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Checkbox
        checked={value}
        color={staged ? 'orange' : undefined}
        disabled={!!reason || saving}
        onChange={(e) => onToggle(e.currentTarget.checked)}
        aria-label={`${label} for ${person.display_name}`}
      />
      {pending.length > 0 && (
        <Box w={8} h={8} bg="blue" style={{ borderRadius: '50%' }} />
      )}
    </Box>
  );
  const tip = reason
    ?? (staged
      ? `Unsaved · ${label}: ${commsValueLabel(value)}. Save changes or Discard at the top.`
        + (pending.length ? ` A change by ${filedBy} is already awaiting approval; saving files another.` : '')
      : pending.length ? `Pending approval · change filed by ${filedBy}` : undefined);
  return tip ? <Tooltip label={tip} withArrow multiline w={280}>{box}</Tooltip> : box;
}
